import { useEffect, useRef, useState, type ChangeEvent, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { FiFileText, FiPlus, FiX } from 'react-icons/fi'

import { PanelHeader } from '@/components/settings/panel-rows'
import { cn } from '@/lib/utils'

const MAX_FILE_SIZE = 10 * 1024 * 1024
const MAX_FILES = 5
const ACCEPTED_TYPES = 'image/png,image/jpeg,image/gif,image/webp,application/pdf,.txt,.log'

interface FeedbackFormProps {
  title: string
  prompt: string
  placeholder: string
  onBack: () => void
  onSubmit?: (message: string, attachments: File[]) => void | Promise<void>
}

interface AttachmentItem {
  id: string
  file: File
  /** Object URL for image previews, null for non-image files. */
  previewUrl: string | null
}

/**
 * Shared Help form — Figma `panel` content for "Share your ideas"
 * (2276:304540) and "Report a bug" (2276:304585). Identical layout, only the
 * heading, prompt and placeholder differ.
 *
 * Send stays disabled until the message has content, matching the design's
 * default (disabled) state. Attachments are optional: any number of files up
 * to MAX_FILES can be added, shown as removable thumbnails with a "+" button.
 */
export function FeedbackForm({
  title,
  prompt,
  placeholder,
  onBack,
  onSubmit,
}: FeedbackFormProps): ReactElement {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const [attachments, setAttachments] = useState<AttachmentItem[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const submittingRef = useRef(false)

  // Always points at the latest list so the unmount cleanup can revoke every URL.
  const attachmentsRef = useRef<AttachmentItem[]>([])
  attachmentsRef.current = attachments

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      })
    }
  }, [])

  const canSend = message.trim().length > 0 && !isSubmitting
  const canAddMore = attachments.length < MAX_FILES

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (files.length === 0) return

    let error: string | null = null
    const accepted: AttachmentItem[] = []
    const freeSlots = MAX_FILES - attachments.length

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        error = t('help.fileTooLarge')
        continue
      }
      if (accepted.length >= freeSlots) {
        error = t('help.tooManyFiles', {
          max: MAX_FILES,
          defaultValue: `Legfeljebb ${MAX_FILES} fájlt csatolhatsz.`,
        })
        break
      }
      accepted.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      })
    }

    setFileError(error)
    if (accepted.length > 0) setAttachments((current) => [...current, ...accepted])
  }

  const removeAttachment = (id: string): void => {
    setAttachments((current) => {
      const target = current.find((item) => item.id === id)
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
      return current.filter((item) => item.id !== id)
    })
    setFileError(null)
  }

  const openFilePicker = (): void => {
    fileInputRef.current?.click()
  }

  const handleSubmit = async (): Promise<void> => {
    if (!canSend || submittingRef.current) return

    submittingRef.current = true
    setIsSubmitting(true)
    try {
      await onSubmit?.(
        message,
        attachments.map((item) => item.file),
      )
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <PanelHeader compact title={title} onBack={onBack} />

      <div data-name="panel-list" className="flex w-full items-center gap-4">
        <p className="hub-title-lg text-list-text min-w-px flex-1">{prompt}</p>
      </div>

      <div data-name="input" className="flex h-25 w-full flex-col items-start gap-1">
        <div className="relative flex min-h-px w-full flex-1 flex-col items-start">
          <textarea
            value={message}
            disabled={isSubmitting}
            onChange={(event) => {
              setMessage(event.target.value)
            }}
            placeholder={placeholder}
            className="hub-resize-textarea bg-btn-secondary-raised hub-stroke-input hub-label-md text-list-text placeholder:text-input-text h-full w-full resize-y rounded-xs px-3 py-1.5 outline-none disabled:cursor-not-allowed disabled:opacity-60"
          />
        </div>
      </div>

      <div data-name="attachment" className="flex w-full flex-col items-start gap-3">
        <p className="hub-title-lg text-list-text w-full">{t('help.attachScreenshot')}</p>

        <div data-name="file-upload" className="flex w-full flex-col items-start gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            onChange={handleFileChange}
            className="hidden"
          />

          {attachments.length === 0 ? (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={openFilePicker}
              className="hub-label-md bg-btn-secondary-raised hub-stroke-input text-list-text hover:bg-btn-border dark:hover:bg-btn-border cursor-pointer rounded-full px-4 py-2 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60 dark:bg-black"
            >
              {t('help.chooseFile')}
            </button>
          ) : (
            <div className="flex w-full flex-wrap items-center gap-3 pt-2">
              {attachments.map((item) => (
                <div key={item.id} className="relative">
                  <div
                    className={cn(
                      'bg-btn-secondary-raised hub-stroke-input flex size-24 items-center justify-center overflow-hidden rounded-md',
                      isSubmitting && 'opacity-60',
                    )}
                    title={item.file.name}
                  >
                    {item.previewUrl ? (
                      <img
                        src={item.previewUrl}
                        alt={item.file.name}
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="text-list-text flex flex-col items-center gap-1 px-2">
                        <FiFileText className="size-6" aria-hidden="true" />
                        <span className="hub-body-sm w-full truncate text-center">
                          {item.file.name}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => {
                      removeAttachment(item.id)
                    }}
                    aria-label={t('help.removeFile')}
                    className="absolute -top-2 -right-2 flex size-7 cursor-pointer items-center justify-center rounded-full bg-black text-white transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-black"
                  >
                    <FiX className="size-4" aria-hidden="true" />
                  </button>
                </div>
              ))}

              {canAddMore && (
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={openFilePicker}
                  aria-label={t('help.addFile', { defaultValue: 'Fájl hozzáadása' })}
                  className="bg-btn-secondary-raised hub-stroke-input text-list-text flex size-12 cursor-pointer items-center justify-center rounded-full transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FiPlus className="size-6" aria-hidden="true" />
                </button>
              )}
            </div>
          )}

          {fileError && <p className="hub-body-sm text-red-500">{fileError}</p>}
        </div>
      </div>

      <div data-name="panel-list" className="flex w-full items-center gap-4 pb-4">
        <p className="hub-body-sm text-list-text-secondary min-w-px flex-1">{t('help.notice')}</p>
      </div>

      <div data-name="panel-list" className="flex w-full items-center justify-end gap-4 pb-4">
        <button
          type="button"
          disabled={!canSend}
          aria-busy={isSubmitting}
          onClick={() => {
            void handleSubmit()
          }}
          className={cn(
            'hub-label-md flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full px-4 py-2 whitespace-nowrap transition-colors',
            canSend
              ? 'bg-btn-primary text-btn-text-inverse hover:opacity-90'
              : 'bg-btn-disabled hub-stroke-disabled text-btn-text-disabled cursor-not-allowed',
          )}
        >
          {isSubmitting && (
            <span
              className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              aria-hidden="true"
            />
          )}
          {isSubmitting ? t('help.sending') : t('help.send')}
        </button>
      </div>
    </>
  )
}
